import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Languages, Lock } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { QueryKey } from '@/Config/api/queryKey';
import { WidgetSettingsDto } from '@/Domain/api/WIdget/dto/WidgetSetting.dto';
import { WidgetTranslationDto } from '@/Domain/api/WIdget/dto/WidgetTranslation.dto';
import { usePreview } from '@/Domain/components/PreviewProvider';
import { Button } from '@/Domain/components/Shared/Button/Button';
import { Separator } from '@/Domain/components/Shared/Separator/Separator';
import { useSubscription } from '@/Domain/components/SubscriptionProvider';
import { useUpdateWidgetSettings } from '@/Domain/hooks/Widget/useUpdateWidgetSettings.hook';
import { PreChatSection } from '@/UI/Website/Appearance/PreChat.accordion';
import { TextContentSection } from '@/UI/Website/Appearance/TextContent.accordion';
import { TranslationTarget } from '@/UI/Website/TextContent/LanguageRail';
import { TranslationsSheet } from '@/UI/Website/TextContent/TranslationsSheet';
import { buildPrimaryFormValues } from '@/UI/Website/TextContent/primaryDefaults';
import { emptyTranslation } from '@/UI/Website/TextContent/translationFormValues';
import { getCurrentScenarioId } from '@/context';

type Props = {
  data: WidgetSettingsDto;
};

const SectionHeader = ({ label, testId }: { label: string; testId?: string }) => (
  <p
    data-testid={testId}
    className="text-xs font-semibold uppercase tracking-wide text-foreground-subtle"
  >
    {label}
  </p>
);

const PrimaryTextContent = ({ data }: { data: WidgetSettingsDto }) => {
  const { t } = useTranslation(['widget', 'common', 'website']);
  const { reset } = usePreview();

  const form = useForm<WidgetSettingsDto>({
    values: buildPrimaryFormValues(data, t),
  });

  useUpdateWidgetSettings({ form, reset });

  return (
    <FormProvider {...form}>
      <form className="flex flex-col gap-6">
        <SectionHeader label={t('widget:text_content')} testId="section-text-content" />
        <TextContentSection />

        <Separator />

        <section>
          <SectionHeader label={t('widget:prechat')} testId="section-prechat" />
          <PreChatSection />
        </section>
      </form>
    </FormProvider>
  );
};

const TranslationsTrigger = ({ enabled, onOpen }: { enabled: boolean; onOpen: () => void }) => {
  const { t } = useTranslation('website');

  return (
    <div
      className="flex items-center justify-between gap-4 rounded-2xl bg-muted p-4"
      data-testid="translations-trigger"
    >
      <div className="flex min-w-0 items-center gap-3">
        <span className="flex size-9 shrink-0 items-center justify-center rounded-full bg-card text-foreground-subtle">
          {enabled ? <Languages className="size-4" /> : <Lock className="size-4" />}
        </span>
        <div className="min-w-0">
          <p className="text-sm font-medium text-foreground">
            {t('text_content_languages.section_title')}
          </p>
          <p className="text-sm text-muted-foreground">
            {t('text_content_languages.section_description')}
          </p>
        </div>
      </div>
      {enabled ? (
        <Button
          variant="secondary"
          size="sm"
          className="shrink-0"
          onClick={onOpen}
          data-testid="open-translations-button"
        >
          {t('text_content_languages.manage_button')}
        </Button>
      ) : (
        <Button
          variant="secondary"
          size="sm"
          asChild
          className="shrink-0"
          data-testid="upgrade-translations-button"
        >
          <Link to="../subscription">{t('text_content_languages.upgrade_cta')}</Link>
        </Button>
      )}
    </div>
  );
};

export const TextContentPage = ({ data }: Props) => {
  const { t } = useTranslation('website');
  const queryClient = useQueryClient();
  const { getConfig } = useSubscription();
  const { refreshConfiguration } = usePreview();

  const enabled = Boolean(getConfig('widget_translations'));

  const [sheetOpen, setSheetOpen] = useState(false);
  const [selection, setSelection] = useState<TranslationTarget | null>(null);

  const { data: translations = [] } = useQuery<WidgetTranslationDto[]>({
    queryKey: [QueryKey.widgetTranslations.list],
    enabled,
  });

  // Show a rail row for the language being added even before its first save
  // creates a row on the server.
  const displayedTranslations = useMemo(() => {
    if (!selection) return translations;
    const exists = translations.some(
      (row) => row.language === selection.language && row.country === selection.country,
    );
    return exists
      ? translations
      : [...translations, emptyTranslation(selection.language, selection.country)];
  }, [translations, selection]);

  const isLocale = (row: TranslationTarget, language: string, country: string) =>
    row.language === language && row.country === country;
  const putLocaleRow = (row: WidgetTranslationDto) =>
    queryClient.setQueryData(
      [QueryKey.widgetTranslations.list],
      (prev: WidgetTranslationDto[] = []) => [
        ...prev.filter((other) => !isLocale(other, row.language, row.country)),
        row,
      ],
    );
  const dropLocaleRow = (language: string, country: string) =>
    queryClient.setQueryData(
      [QueryKey.widgetTranslations.list],
      (prev: WidgetTranslationDto[] = []) =>
        prev.filter((row) => !isLocale(row, language, country)),
    );

  const deleteMutation = useMutation<
    void,
    unknown,
    { language: string; country: string },
    { previous?: WidgetTranslationDto[] }
  >({
    mutationKey: [QueryKey.widgetTranslations.delete],
    // Remove optimistically: the auto-select effect below reads this cache, so a row
    // that lingers until the refetch gets re-selected and displayedTranslations
    // resurrects the just-deleted language as a phantom rail entry.
    onMutate: ({ language, country }) => {
      const previous = queryClient.getQueryData<WidgetTranslationDto[]>([
        QueryKey.widgetTranslations.list,
      ]);
      dropLocaleRow(language, country);
      return { previous };
    },
    onError: (_error, _row, context) => {
      if (context?.previous) {
        queryClient.setQueryData([QueryKey.widgetTranslations.list], context.previous);
      }
      toast.error(t('appearance.form.error.common'));
    },
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: [QueryKey.widgetTranslations.list] }),
  });

  // Create-only; handleAdd writes its outcome, since only it knows whether the attempt still owns the locale.
  const createMutation = useMutation<WidgetTranslationDto, unknown, WidgetTranslationDto>({
    mutationKey: [QueryKey.widgetTranslations.create],
  });

  const generateMutation = useMutation<
    WidgetTranslationDto,
    unknown,
    { language: string; country: string }
  >({
    mutationKey: [QueryKey.widgetTranslations.generate],
  });

  // Generation takes seconds, and the scenario header is re-resolved from the URL on every
  // request. Without this, switching agent mid-flight would save one agent's text into
  // another, and removing the language while it generated would resurrect it.
  //
  // Keyed by locale but holding a per-attempt token, because the key alone cannot tell two
  // attempts apart: delete-then-re-add reinserts the same key, and the first attempt would
  // otherwise consume the second's authorization and write its own stale text.
  const pendingAddsRef = useRef(new Map<string, number>());
  const attemptCounterRef = useRef(0);
  // Locales removed while their add was still settling. The DELETE fired on removal can
  // reach the server before the attempt's create-only POST, which then recreates the row;
  // these keys make the removal authoritative until the user asks for the locale again.
  const cancelledAddsRef = useRef(new Set<string>());
  // Reactive mirror of the map's keys, so pending state is per locale: with two adds in
  // flight, the first settling must not clear the second's skeletons.
  const [pendingKeys, setPendingKeys] = useState<readonly string[]>([]);
  const addKey = (scenario: string, language: string, country: string) =>
    `${scenario}|${language}|${country}`;
  const syncPendingKeys = () => setPendingKeys(Array.from(pendingAddsRef.current.keys()));

  const handleAdd = async (language: string) => {
    const scenario = getCurrentScenarioId();
    const key = addKey(scenario, language, '');
    // Asking for the locale again supersedes an earlier removal, so a still-settling
    // cancelled attempt must no longer delete what this add produces.
    cancelledAddsRef.current.delete(key);
    setSelection({ language, country: '' });
    if (translations.some((row) => isLocale(row, language, ''))) return;

    const attempt = ++attemptCounterRef.current;
    pendingAddsRef.current.set(key, attempt);
    syncPendingKeys();
    // Ownership lapses on removal and passes to a re-add; only the owner may release or write.
    const owns = () => pendingAddsRef.current.get(key) === attempt;
    const release = () => {
      if (!owns()) return;
      pendingAddsRef.current.delete(key);
      syncPendingKeys();
    };

    // Generating is best effort: on failure the language is still added, with the empty
    // row the user can fill in themselves, rather than blocking the add.
    let row = emptyTranslation(language, '');
    try {
      row = await generateMutation.mutateAsync({ language, country: '' });
    } catch {
      if (owns()) {
        toast.error(t('text_content_languages.generate_failed'));
      }
    }

    if (!owns() || getCurrentScenarioId() !== scenario) {
      release();
      return;
    }

    // Cheap same-tab skip; the real cross-tab guard is the create-only POST, which keeps
    // an existing row untouched however delayed this completion is.
    const latest =
      queryClient.getQueryData<WidgetTranslationDto[]>([QueryKey.widgetTranslations.list]) ?? [];
    if (latest.some((r) => isLocale(r, language, ''))) {
      release();
      return;
    }

    // Held until the create settles, not just until generation does. The row stays
    // skeletoned - so uneditable, so unsaveable - until the server has arbitrated whose
    // row wins, and the one it kept has been applied.
    putLocaleRow(row);
    let serverRow: WidgetTranslationDto | undefined;
    let failed = false;
    try {
      serverRow = await createMutation.mutateAsync(row);
    } catch {
      failed = true;
    }

    const sameScenario = getCurrentScenarioId() === scenario;
    if (owns() && sameScenario) {
      if (failed) {
        // Roll back this locale only, and drop its selection so displayedTranslations cannot re-add it.
        dropLocaleRow(language, '');
        setSelection((prev) => (prev && isLocale(prev, language, '') ? null : prev));
        toast.error(t('appearance.form.error.common'));
      } else {
        if (serverRow) putLocaleRow(serverRow);
        queryClient.invalidateQueries({ queryKey: [QueryKey.widgetTranslations.list] });
      }
    } else if (!sameScenario) {
      // The list cache is shared across agents; after a switch only a refetch is a safe write.
      queryClient.invalidateQueries({ queryKey: [QueryKey.widgetTranslations.list] });
    }
    // If the user removed the language while the create was in flight, its DELETE may
    // have reached the server first and removed nothing, letting the POST persist the
    // row afterwards. Deleting again now that the create has settled makes the removal
    // win under either server ordering; when the removal already won, this deletes 0.
    // Scenario-guarded like the pre-create check: the request resolves the scenario
    // from the URL, and after a switch it would delete another agent's row.
    if (cancelledAddsRef.current.has(key) && getCurrentScenarioId() === scenario) {
      deleteMutation.mutate({ language, country: '' });
    }
    release();
  };

  // Auto-select the first language once the sheet is open and translations have
  // loaded (the list may resolve after the sheet opens, so this can't be a
  // one-shot on open).
  useEffect(() => {
    if (sheetOpen && !selection && translations.length > 0) {
      setSelection({ language: translations[0].language, country: translations[0].country });
    }
  }, [sheetOpen, selection, translations]);

  const handleOpenChange = (open: boolean) => {
    setSheetOpen(open);
    // Restore the preview to the saved primary config when closing the sheet.
    if (!open) refreshConfiguration();
  };

  const handleDelete = (language: string, country: string) => {
    const key = addKey(getCurrentScenarioId(), language, country);
    // With the add still settling, cancelling the local attempt is not enough: its
    // create-only POST may already be racing this DELETE on the server, so mark the
    // removal as the newest intent for the locale before cancelling.
    if (pendingAddsRef.current.has(key)) {
      cancelledAddsRef.current.add(key);
    }
    // Cancels a generation still in flight for this language, so it cannot re-add it.
    pendingAddsRef.current.delete(key);
    syncPendingKeys();
    deleteMutation.mutate({ language, country });
    setSelection((prev) => {
      if (!prev || prev.language !== language || prev.country !== country) return prev;
      const next = translations.find(
        (row) => !(row.language === language && row.country === country),
      );
      return next ? { language: next.language, country: next.country } : null;
    });
  };

  return (
    <div className="flex flex-col gap-6">
      <TranslationsTrigger enabled={enabled} onOpen={() => handleOpenChange(true)} />
      {enabled && (
        <TranslationsSheet
          open={sheetOpen}
          onOpenChange={handleOpenChange}
          data={data}
          translations={translations}
          displayedTranslations={displayedTranslations}
          selection={selection}
          onSelect={setSelection}
          onAdd={handleAdd}
          onDelete={handleDelete}
          isGenerating={
            !!selection &&
            pendingKeys.includes(
              addKey(getCurrentScenarioId(), selection.language, selection.country),
            )
          }
        />
      )}

      <PrimaryTextContent data={data} />
    </div>
  );
};
